import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import style from './styles'
import { ScrollableList, DropdownMenu, AdSlot } from '..'
import Skeleton from '../Skeleton/Skeleton'

const Tabs = ({
  tabs, children, subTab, programmeTab, programmeGuid, adSlotProps, isLive,
}) => {
  const [activeId, setActiveId] = useState(null)
  const [mouseFocus, setMouseFocus] = useState(false)
  const [mobile, setMobile] = useState(false)
  const [sortMenuTitle, setSortMenuTitle] = useState(tabs[0]?.title)
  const [loading, setLoading] = useState(true)

  const links = useRef(tabs.map(() => React.createRef()))
  let mq = null // for SSR

  useEffect(() => {
    if (!tabs.length) return () => {}

    setActiveTab()
    mq = window.matchMedia('(min-width: 768px)')
    mq.addListener(handleMediaQueryChange)
    handleMediaQueryChange()
    window.addEventListener('hashchange', setActiveTab)
    return () => {
      mq.removeListener(handleMediaQueryChange)
      window.removeEventListener('hashchange', setActiveTab)
    }
  }, [])

  useEffect(() => {
    // Set loading to true when active tab changes
    setLoading(true)
    const timer = setTimeout(() => {
      setLoading(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [activeId])

  const handleMediaQueryChange = () => {
    setMobile(!mq.matches)
  }

  const subMenuClose = (e) => {
    e.preventDefault()
    setSortMenuTitle(e.target.innerText)
  }

  const setActiveTab = () => {
    const hash = window.location.hash?.replace('#', '')
    const test = tabs.find(tab => tab?.id === hash)
    const newActiveId = test ? hash : tabs?.[0].id
    setActiveId(newActiveId)
  }

  const handleClickDropDown = (e) => {
    e.preventDefault()
    if (e.target.id !== 'sectionId') {
      handleMousedown()
      setActiveId(e.target.id)
      window.history.pushState(null, null, `#${e.target.id}`)
    }
  }

  const handleClick = (e, tab) => {
    e.preventDefault()
    if (tab.id === 'applyTab' && tab.link) {
      window.open(tab.link, '_blank')
      return
    }
    handleMousedown()
    setActiveId(tab.id)
    window.history.pushState(null, null, `#${tab.id}`)
  }

  const handleKeyDown = (e, tab) => {
    const index = tabs.indexOf(tab)
    let newIndex = null
    if (e.which === 37) newIndex = index - 1 // left arrow
    if (e.which === 39) newIndex = index + 1 // right arrow
    if (newIndex === null) return
    e.preventDefault()
    if (newIndex >= tabs.length) newIndex = 0
    if (newIndex < 0) newIndex = tabs.length - 1
    const newId = tabs[newIndex].id
    setActiveId(newId)
    const link = links.current[newIndex]?.current
    link.focus()
  }

  const handleMousedown = () => {
    setMouseFocus(true)
  }

  const handleBlur = () => {
    setMouseFocus(false)
  }

  return (
    <div className={`Tabs ${isLive ? 'live' : ''}`} style={subTab ? { padding: '0px', border: 'none' } : {}}>
      {subTab && mobile && (
        <div
          role="button"
          tabIndex={0}
          aria-label="dropdown button"
          className="sub-menu"
          onClick={e => handleClickDropDown(e)}
          onKeyDown={e => handleClickDropDown(e)}
          onBlur={handleBlur}
        >
          <DropdownMenu
            title={sortMenuTitle}
            menuItems={tabs}
            subTab={subTab}
            closeMenu={subMenuClose}
          />
        </div>
      )}
      <div className="wrap">
        {programmeTab && !isLive && (
          <div className="sponsor">
            <AdSlot type="sponsor-logo" path="summary" programmeOrChannelGuid={programmeGuid} adSlotProps={adSlotProps} />
          </div>
        )}
        {(!subTab || (subTab && !mobile)) && (
          <ScrollableList role="tablist" className="tab-list" subTab={subTab}>
            {tabs.map((tab, i) => {
              const href = (tab.id === 'applyTab' && tab.link) ? tab.link : `#${tab.id}`
              return (
                <li
                  role="presentation"
                  key={tab.title}
                  style={subTab ? { border: 'none' } : {}}
                  className={tab.id === activeId ? 'active' : ''}
                >
                  <a
                    id={`${tab.id}-tab`}
                    role="tab"
                    href={href}
                    className={mouseFocus ? 'nofocus' : ''}
                    style={subTab ? { fontSize: '20px' } : {}}
                    onClick={e => handleClick(e, tab)}
                    onBlur={handleBlur}
                    aria-selected={tab.id === activeId}
                    tabIndex={tab.id === activeId ? null : -1}
                    onKeyDown={e => handleKeyDown(e, tab)}
                    ref={links.current[i]}
                  >
                    {tab.title}
                  </a>
                </li>
              )
            })}
          </ScrollableList>
        )}
      </div>
      <div className={`tabs-content ${isLive ? 'live' : ''}`}>
        {loading && programmeTab ? (
          <div className="scrollable-loader">
            <div className="loader">
              <Skeleton classes="programme" />
              <Skeleton classes="programme" />
            </div>
          </div>
        ) : (
          children && React.Children.map(children, child => (
            // clone the child tabs and add the active prop
            React.cloneElement(child, { active: activeId === child.props.id })
          ))
        )}
      </div>
      <style jsx>{style}</style>
    </div>
  )
}
Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })
  ),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  subTab: PropTypes.bool,
  programmeTab: PropTypes.bool,
  programmeGuid: PropTypes.string,
  adSlotProps: PropTypes.object,
  isLive: PropTypes.bool,
}
Tabs.defaultProps = {
  tabs: [],
  children: null,
  subTab: false,
  programmeGuid: '',
  programmeTab: false,
  adSlotProps: {},
  isLive: false,
}
export default Tabs
